import { Typography } from '@mui/material';
import { AxiosResponse } from 'axios';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ConfirmationMessageModal,
  DateState,
  DNCLayout,
  MenuItem,
  Table,
  ScrollToTop,
  TableHead,
  TableActionsMenu,
  FullScreenLoader,
} from '@Components/index';
import { CommonConstants } from '@Constants/index';
import {
  getGlobalDncRows,
  getExportFilters,
  getDncListQuery,
} from '@Containers/Dnc/Dnc.utils';
import DncModal from '@Containers/Dnc/DncModal';
import { DncList } from '@Models/dnc.models';
import {
  useBulkDeleteFromGlobalDncToResyncMutation,
  useDeleteFromGlobalDncToResyncMutation,
  useUpdateGlobalDncToResyncMutation,
} from '@Services/dataIntegration.service';
import dncExportService from '@Services/dncExport.service';
import {
  useDeleteBulkGlobalDncRowsMutation,
  useDeleteGlobalDncRowsMutation,
  useGetGlobalDncListQuery,
  useRestoreGlobalDncRowMutation,
} from '@Services/globalDnc.service';
import {
  BaseAlertProps,
  DialerCrudTypes,
  HeaderCheckboxState,
  Icon,
  ConfirmationActionTypes,
} from '@Typings/global.types';
import {
  downloadCsv,
  getDateFromISODateTimeString,
  getModalBtnComponent,
  getRowsPerPageOptions,
  getSortingIcon,
  getCheckableCount,
  unformatUSNumber,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import useRoute from '@Utils/hooks/useRoute.hook';

const DNC: FunctionComponent = (): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { changeRoute } = useRoute();
  const { triggerAlert } = useAlert();

  /** STATES */
  const [searchValue, setSearchValue] = useState<string>('');
  const [dateState, setDateState] = useState<[DateState]>([
    {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    },
  ]);
  const [checked, setChecked] = useState<readonly string[]>([]);
  const [modalCrudType, setModalCrudType] = useState<DialerCrudTypes | null>(
    null
  );
  const [dncId, setDncId] = useState<string>('');
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(0);
  const [showFilterBtn, setShowFilterBtn] = useState<boolean>(false);
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [headerCheckboxState, setHeaderCheckboxState] =
    useState<HeaderCheckboxState>({ checked: false, indeterminate: false });
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] =
    useState<React.ReactNode | null>(null);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  const [isBulkAction, setIsBulkAction] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  /** Syncs the state according to URL Query Params */
  const syncState = useCallback(() => {
    /** URL PARAMS */
    const pageNo =
      ((query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) as number) ||
        CommonConstants.INITIAL_PAGE_NO) - 1;
    const pageSize =
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) as number) ||
      CommonConstants.INITIAL_ROWS_PER_PAGE;
    const phoneParam =
      (query.get(CommonConstants.QUERY_PARAMS.DNC.PHONE) as string) || '';
    const dateStartParam = getDateFromISODateTimeString(
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.DATE_START) as string) ||
        ''
    );
    const dateEndParam = getDateFromISODateTimeString(
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.DATE_END) as string) || ''
    );

    setPage(pageNo);
    setRowsPerPage(pageSize);
    setSearchValue(phoneParam);
    setDateState((prevState) => [
      {
        ...prevState[0],
        startDate: dateStartParam,
        endDate: dateEndParam,
      },
    ]);
    setShowFilterBtn(!!(phoneParam || dateStartParam || dateEndParam));
  }, [setPage, setRowsPerPage]);

  useEffect(() => {
    syncState();
  }, [syncState, location.search]);

  /** Handle Query Data -->  */
  const DncListQuery: DncList = useMemo(
    () => getDncListQuery(query.parse(location.search)),
    [location.search]
  );
  /** <-- Handle Query Data   */

  /** EDGE CASE
   * Append Required URL Params
   */
  useEffect(() => {
    const requiredParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) ||
        `${CommonConstants.INITIAL_ROWS_PER_PAGE}`,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) ||
        `${CommonConstants.INITIAL_PAGE_NO}`,
      [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER) ||
        `${CommonConstants.INITIAL_DNC_SORT_ORDER}`,
      [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.DIRECTION) ||
        `${CommonConstants.INITIAL_SORT_DIRECTION}`,
    });

    changeRoute(requiredParams);
  }, [changeRoute]);

  /** RTK Query Hooks */
  const {
    data,
    isLoading,
    isFetching,
    error: dncRowsError,
  } = useGetGlobalDncListQuery(DncListQuery);
  const [restoreRow, restoreRowMethods] = useRestoreGlobalDncRowMutation();
  const [deleteRows, deleteRowsMethods] = useDeleteGlobalDncRowsMutation();
  const [deleteFromGlobalDncToResync] =
    useDeleteFromGlobalDncToResyncMutation();
  const [updateGlobalDncToResync] = useUpdateGlobalDncToResyncMutation();
  const [deleteBulkRows, deleteBulkRowsMethods] =
    useDeleteBulkGlobalDncRowsMutation();
  const [bulkDeleteFromGlobalDncToResync] =
    useBulkDeleteFromGlobalDncToResyncMutation();

  /** Loading State */
  const loading =
    isLoading ||
    isFetching ||
    restoreRowMethods.isLoading ||
    deleteRowsMethods.isLoading ||
    deleteBulkRowsMethods.isLoading ||
    exportLoading;

  /** Handle Error and Success Alert  -->
   */
  useEffect(() => {
    setIsBulkAction(false);
    setDncId('');
    setPhoneNumber('');

    // QUERY ERROR
    if (dncRowsError) {
      triggerAlert(dncRowsError, setAlertState);
    }
  }, [dncRowsError, triggerAlert]);

  useEffect(() => {
    // DELETE SUCCESS
    if (deleteRowsMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.GLOBAL_DNC_DELETE,
        setAlertState
      );
      setChecked([]); // Reset Checked state
    }

    // DELETE ERROR
    if (deleteRowsMethods.error) {
      triggerAlert(deleteRowsMethods.error, setAlertState);
    }
  }, [deleteRowsMethods.isSuccess, deleteRowsMethods.error, triggerAlert]);
  /** <-- Handle Alert */

  /** Handle Error and Success Alert  -->
   * For BULK DELETE
   */
  useEffect(() => {
    // BULK DELETE SUCCESS
    if (deleteBulkRowsMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.GLOBAL_DNC_DELETE,
        setAlertState
      );
      setItemsCount(0); // Reset Items Count
    }

    // BULK DELETE ERROR
    if (deleteBulkRowsMethods.error) {
      triggerAlert(deleteBulkRowsMethods.error, setAlertState);
    }
  }, [
    deleteBulkRowsMethods.isSuccess,
    deleteBulkRowsMethods.error,
    triggerAlert,
  ]);
  /** <-- Handle Alert */

  /** Handle Error and Success Alert  -->
   * For RESTORE
   */
  useEffect(() => {
    // RESTORE SUCCESS
    if (restoreRowMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.GLOBAL_DNC_RESTORE,
        setAlertState
      );
    }

    // RESTORE ERROR
    if (restoreRowMethods.error) {
      triggerAlert(restoreRowMethods.error, setAlertState);
    }
  }, [restoreRowMethods.isSuccess, restoreRowMethods.error, triggerAlert]);
  /** <-- Handle Alert */

  /** Clears all checkboxes. */
  const clearSelection = (): void => {
    // Allows header checkbox to update when all rows are disabled
    setHeaderCheckboxState({ checked: false, indeterminate: false });
    setItemsCount(0);
    setChecked([]);
  };

  /** Handle Filter Button Visibility and Date state
   */
  const getDateState = (startDate?: Date, endDate?: Date) => {
    const dateParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_START]:
        startDate?.toISOString() || '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_END]:
        endDate?.toISOString() || '',
    });

    changeRoute(dateParams);

    // Clear bulk selection (if any) when user applies search filter
    clearSelection();
  };

  /** Handle Delete Row */
  const handleDeleteRow = async (id: string, phone: string) => {
    setDncId(id);
    setPhoneNumber(phone);
    await deleteRows([id]);

    deleteFromGlobalDncToResync(phone).catch((error) => {
      console.log('Error with global delete request to Resync service:', error);
    });
  };

  /** Handle Restore Row */
  const handleRestoreRow = async (id: string, phone: string) => {
    setDncId(id);
    setPhoneNumber(phone);
    await restoreRow(id);

    updateGlobalDncToResync({ phone, prevPhone: null }).catch((error) => {
      console.log(
        'Error with global restore request to Resync service:',
        error
      );
    });
  };

  /** Handle Edit Row */
  const handleEditRow = (id: string, phone: string) => {
    setDncId(id);
    setPhoneNumber(unformatUSNumber(phone));
    setModalCrudType(DialerCrudTypes.Edit);
  };

  /** Table Actions Component */
  const actionsMenuComponent = (
    id: string,
    disabled: boolean,
    phone: string
  ): JSX.Element => {
    /** Handles click on actions component delete option */
    const handleDeleteActionClick = (actionType: ConfirmationActionTypes) => {
      const message = (
        <Typography
          variant="main"
          sx={{
            '& .phone': {
              color: 'secondary.main',
            },
          }}
        >
          <Trans
            values={{ phone }}
            i18nKey={
              actionType === ConfirmationActionTypes.Delete
                ? 'common.confirmationMessages.confirmDelete'
                : 'common.confirmationMessages.confirmRestore'
            }
            components={{
              strong: <strong />,
              span: <span className="phone" />,
            }}
          />
        </Typography>
      );
      setConfirmationActionType(actionType);
      setConfirmationMessage(message);
      setIsConfirmationModalOpen(true);
      setPhoneNumber(phone);
    };

    /** Actions Menu Data */
    const actionsMenu: MenuItem[] = [
      {
        label: t('common.messages.Edit'),
        icon: 'edit',
        value: id,
        onClick: () => handleEditRow(id, phone),
      },
      {
        label: t('common.messages.Delete'),
        icon: 'delete',
        value: id,
        onClick: () => {
          setDncId(id);
          handleDeleteActionClick(ConfirmationActionTypes.Delete);
          setPhoneNumber(unformatUSNumber(phone));
        },
      },
    ];

    /** Actions Menu for Deleted Data */
    const actionsMenuDeleted: MenuItem[] = [
      {
        label: t('common.actionsMenu.Restore'),
        icon: 'restore',
        value: id,
        onClick: () => {
          setDncId(id);
          handleDeleteActionClick(ConfirmationActionTypes.Restore);
          setPhoneNumber(unformatUSNumber(phone));
        },
      },
    ];

    return (
      <TableActionsMenu
        menuItems={disabled ? actionsMenuDeleted : actionsMenu}
      />
    );
  };

  /** Table Modal Button Component Function
   *
   * Returns the Modal Button component which opens modal when clicked.
   */
  const modalBtnComponent = (
    id: string,
    phone: string,
    name: string,
    disabled: boolean
  ): JSX.Element => {
    const handleClick = () => {
      setModalCrudType(DialerCrudTypes.View);
      setDncId(id);
      setPhoneNumber(unformatUSNumber(phone));
    };

    return getModalBtnComponent({
      id,
      name,
      onClick: handleClick,
      isDisabled: disabled,
    });
  };

  /** Rows, Total Rows Count */
  const rows = getGlobalDncRows(
    data?.items,
    actionsMenuComponent,
    modalBtnComponent
  );
  const totalRowsCount = data?.total || 0;
  const checkableCount = getCheckableCount(rows); // count of non disabled rows
  const phonesNumberMapRef = useRef<{ [key: string]: string }>({});

  useEffect(() => {
    rows.forEach((item) => {
      const phone = item?.data?.phone;
      if (!phonesNumberMapRef.current[item.id]) {
        if (phone && typeof phone === 'object' && 'props' in phone) {
          phonesNumberMapRef.current[item.id] = unformatUSNumber(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            phone.props.text
          );
        }
      }
    });
  }, [rows]);

  const phonesNumberMap = phonesNumberMapRef.current;

  /** Bulk Operation Handler
   * Handles Delete.
   */
  const handleBulkDeleteBtnClick = async () => {
    if (itemsCount) {
      return deleteBulkRows({ ...DncListQuery, itemsCount });
    }
    await deleteRows(checked);
    bulkDeleteFromGlobalDncToResync(
      checked.map((id) => phonesNumberMap[id])
    ).catch((error) => {
      console.log(
        'Error with global bulk delete request to Resync service: ',
        error
      );
    });
  };

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    switch (confirmationActionType) {
      case ConfirmationActionTypes.Delete:
        return isBulkAction
          ? handleBulkDeleteBtnClick()
          : handleDeleteRow(dncId, phoneNumber);
      case ConfirmationActionTypes.Restore:
        return handleRestoreRow(dncId, phoneNumber);
      default:
        break;
    }
  };

  /** Pagination Select Rows Options */
  const rowsPerPageOptions = getRowsPerPageOptions(
    CommonConstants.INITIAL_ROWS_PER_PAGE,
    totalRowsCount
  );

  /** Table Head Checkbox Menu Option 1
   * Selects the current Page Checkboxes.
   */
  const selectPage = (): void => {
    clearSelection();
    setHeaderCheckboxState({ checked: true, indeterminate: false });
    if (setChecked) {
      setChecked(rows.filter((row) => !row.disabled).map((row) => row.id));
    }
  };

  /** Table Head Checkbox Menu Option 2
   * Selects the first 1000 Checkboxes.
   */
  const select1000 = (): void => {
    clearSelection();
    setItemsCount(Math.min(totalRowsCount, CommonConstants.BULK_COUNT));
  };

  /** Table Head Checkbox Handler */
  const headerCheckClickHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (event.target.checked && !headerCheckboxState.indeterminate) {
      selectPage();
    } else {
      clearSelection();
    }
  };

  /** Table Head Checkbox Menu Options */
  const menuOptions: MenuItem[] = [
    {
      label: t('common.table.Select Page'),
      value: 'page',
      onClick: selectPage,
    },
    {
      label: t('common.table.Select first 1000'),
      value: '1000',
      onClick: select1000,
    },
    {
      label: t('common.table.Clear Selection'),
      value: 'clear',
      onClick: clearSelection,
    },
  ];

  /** Filter Handler
   * Handles Search Button based Search.
   */
  const onSearch = (value: string) => {
    const searchParam = query.set(
      CommonConstants.QUERY_PARAMS.DNC.PHONE,
      value
    );
    changeRoute(searchParam);

    // Clear bulk selection (if any) when user applies search filter
    clearSelection();
  };

  /** Handle Data Exporting and export loading */
  const handleExportRows = (exportAll?: DncList) => {
    setExportLoading(true);

    // Add filters for exporting
    const exportMetaData =
      exportAll || getExportFilters(DncListQuery, checked, itemsCount);

    dncExportService
      .exportDncRows(exportMetaData, true)
      .then((res: AxiosResponse<string>) => {
        if (res.status === 200) {
          if (res.data) {
            downloadCsv(res.data, 'global_dnc');
            triggerAlert(
              CommonConstants.GLOBAL_MESSAGES.GLOBAL_DNC_EXPORT,
              setAlertState,
              'success'
            );
          }
          setChecked([]);
        }
      })
      .catch(() => {
        triggerAlert('GENERIC_ERROR_MESSAGE', setAlertState, 'error');
      })
      .finally(() => {
        setExportLoading(false);
        setItemsCount(0);
      });
  };

  /** Handle Data Exporting and export loading for all records */
  const handleExportAll = () => {
    handleExportRows({ ...DncListQuery, itemsCount: totalRowsCount });
  };

  /** Bulk Operation Handler
   * Handles Cancel
   */
  const handleCancelBtnOnClick = () => {
    clearSelection();
  };

  /** Handle Add New Number */
  const handleAddNumberBtnClick = () => {
    setModalCrudType(DialerCrudTypes.Add);
    setDncId('');
    setPhoneNumber('');
  };

  /** CLEAR FILTER BUTTON CLICK HANDLER */
  const handleClearFilterBtnClick = () => {
    const params = query.setAll({
      [CommonConstants.QUERY_PARAMS.DNC.PHONE]: '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_START]: '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_END]: '',
    });

    changeRoute(params);
  };

  /** Pagination Handler
   * HANDLES PAGE CHANGE */
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    const pageParam = query.set(
      CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
      newPage + 1
    );
    changeRoute(pageParam);
  };

  /** Pagination Handler
   * HANDLES PAGE SIZE CHANGE */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    const pageParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]: newRowsPerPage,
    });
    changeRoute(pageParams);
  };

  /** TABLE HEAD */
  const getTableHead = (): TableHead[] => {
    const orderParam = query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER);
    const directionParam = query.get(
      CommonConstants.QUERY_PARAMS.COMMON.DIRECTION
    ) as string;

    /** Handle Sorting Direction */
    const handleTableHeadClick = (order: string) => {
      const param = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
          order || CommonConstants.INITIAL_DNC_SORT_ORDER,
        ...(order === orderParam
          ? {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                directionParam === CommonConstants.INITIAL_SORT_DIRECTION
                  ? CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC
                  : CommonConstants.INITIAL_SORT_DIRECTION,
            }
          : {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                CommonConstants.INITIAL_SORT_DIRECTION,
            }),
      });

      changeRoute(param);
    };

    /** Handle Sorting Icon */
    const getTableHeadIcon = (order: string): Icon =>
      order === orderParam ? getSortingIcon(directionParam) : 'sorting';

    return [
      {
        label: t('common.table.Global DNC Number'),
        icon: getTableHeadIcon(CommonConstants.DNC_SORT.PHONE),
        width: 22.2,
        onClick: () => handleTableHeadClick(CommonConstants.DNC_SORT.PHONE),
      },
      {
        label: t('common.table.Created Date'),
        icon: getTableHeadIcon(CommonConstants.DNC_SORT.CREATE_DATE),
        align: 'center',
        width: 17.2,
        onClick: () =>
          handleTableHeadClick(CommonConstants.DNC_SORT.CREATE_DATE),
      },
      {
        label: t('common.table.Last Updated'),
        icon: getTableHeadIcon(CommonConstants.DNC_SORT.LAST_UPDATE_DATE),
        width: 17.2,
        align: 'center',
        onClick: () =>
          handleTableHeadClick(CommonConstants.DNC_SORT.LAST_UPDATE_DATE),
      },
      {
        label: t('common.table.Reasons'),
        icon: getTableHeadIcon(CommonConstants.DNC_SORT.REASON),
        width: 17.2,
        onClick: () => handleTableHeadClick(CommonConstants.DNC_SORT.REASON),
      },
      {
        label: t('common.table.Comments'),
        width: 31,
      },
      {
        label: t('common.table.Actions'),
        width: 8.5,
        align: 'center',
      },
    ];
  };

  const deleteBtnPropsOnClick = () => {
    setIsConfirmationModalOpen(true);
    setIsBulkAction(true);
    setConfirmationActionType(ConfirmationActionTypes.Delete);

    const message = (
      <Typography>
        <Trans
          i18nKey="common.confirmationMessages.Delete Confirmation"
          components={{ strong: <strong /> }}
        />
      </Typography>
    );
    setConfirmationMessage(message);
  };

  /** HANDLES THE TABLE HEADER CHECKBOX STATE (checked and indeterminate) */
  useEffect(() => {
    if (!loading) {
      if (checked?.length && checked.length !== checkableCount) {
        setHeaderCheckboxState({ checked: false, indeterminate: true });
      } else if (checked?.length && checked.length === checkableCount) {
        setHeaderCheckboxState({ checked: true, indeterminate: false });
      } else {
        setHeaderCheckboxState({ checked: false, indeterminate: false });
      }
    }
  }, [checked?.length, loading, checkableCount]);

  // TODO: (Sachin) Refactor Global Dnc Container Same as Dnc Container
  /** DNC Table Component */
  const TableComponent = (
    <Table
      rows={rows}
      tableHead={getTableHead()}
      checked={checked}
      setChecked={setChecked}
      checkboxMenuProps={{
        menuOptions,
        checkboxProps: {
          checked: headerCheckboxState.checked,
          indeterminate: headerCheckboxState.indeterminate,
          onChange: headerCheckClickHandler,
        },
      }}
      paginationProps={{
        page,
        rowsPerPage,
        count: totalRowsCount,
        rowsPerPageOptions,
        onPageChange: handleChangePage,
        onRowsPerPageChange: handleChangeRowsPerPage,
      }}
      loading={loading}
    />
  );
  /** Table */

  return !isLoading ? (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <DncModal
        isGlobal
        dncId={dncId}
        phoneNumber={phoneNumber}
        isLoading={isLoading}
        crudType={modalCrudType}
        setCrudType={setModalCrudType}
        deleteBtnOnClick={() => handleDeleteRow(dncId, phoneNumber)}
      />
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={confirmationMessage}
        onClick={handleConfirmationModalClick}
      />
      <DNCLayout
        heading={t('dnc.Global Do Not Call Numbers')}
        alertState={alertState}
        searchProps={{
          value: searchValue,
          setValue: setSearchValue,
          placeholder: t('common.tableFilters.Search by number'),
          onSearch,
        }}
        deleteBtnProps={{
          onClick: () => deleteBtnPropsOnClick(),
        }}
        dateRangeProps={{
          dateState,
          setDateState,
          getDateState,
        }}
        cancelBtnProps={{
          onClick: () => handleCancelBtnOnClick(),
        }}
        showFilterControls={showFilterBtn}
        selectedBtnProps={{
          onClick: clearSelection,
        }}
        dncAddBtnClick={() => handleAddNumberBtnClick()}
        exportBtnClick={() => handleExportRows()}
        exportAllClick={() => handleExportAll()}
        clearFilterBtnProps={{
          onClick: () => handleClearFilterBtnClick(),
          disabled: isLoading,
        }}
        selectedBtnNumber={itemsCount || checked.length}
        table={TableComponent}
      />
    </>
  ) : (
    <FullScreenLoader />
  );
};

export default DNC;
